// Sentry side-effect import is wrapped in try/catch in `sentry.js` itself —
// it can NEVER throw at module-load. Production-safe.
import "@/sentry";
import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n"; // initialises i18next before any component renders
import App from "@/App";
import { Sentry, sentryEnabled } from "@/sentry";

const FallbackUI = ({ resetError }) => (
  <div style={{ padding: 24, maxWidth: 520, margin: "60px auto", fontFamily: "Work Sans, system-ui, sans-serif" }}>
    <div style={{ color: "#ff4500", fontWeight: 700, letterSpacing: "0.22em", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>Something went wrong</div>
    <h1 style={{ fontSize: 28, marginBottom: 12 }}>We've logged the issue.</h1>
    <p style={{ color: "#555", lineHeight: 1.5, marginBottom: 24 }}>
      Sorry — something unexpected happened. The team's been notified. Try refreshing or head back to the homepage.
    </p>
    <button
      onClick={resetError}
      style={{ background: "#ff4500", color: "#fff", border: 0, padding: "10px 18px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 12, cursor: "pointer" }}
    >
      Try again
    </button>
  </div>
);

// Defensive render: ErrorBoundary is only wrapped when Sentry truly loaded.
// If `Sentry.ErrorBoundary` is somehow missing (CDN block, ad-blocker, broken
// build) we still render <App />. Never blank-screen because of Sentry.
const root = ReactDOM.createRoot(document.getElementById("root"));
const Boundary = (sentryEnabled && Sentry && Sentry.ErrorBoundary) || null;
root.render(
  Boundary ? (
    <Boundary fallback={FallbackUI}>
      <App />
    </Boundary>
  ) : (
    <App />
  ),
);

// ---------------------------------------------------------------------------
// Service-worker registration with aggressive auto-update.
//
// Why: every device caches its own shell HTML + JS until the SW notices a
// new version. Browsers only check `sw.js` opportunistically (~every 24 h
// by default; installed PWAs sometimes longer). Without explicit polling,
// a deploy on Tuesday won't reach grandma's tablet until Friday.
//
// Strategy (iter50):
//   1. On load → register + immediate registration.update() check.
//   2. Listen for `updatefound` → when the new SW reaches `installed`
//      state with a controller already present, tell it to skipWaiting
//      and reload. This is the "new version available" trigger that
//      doesn't wait for the next page-navigation.
//   3. Re-check on `visibilitychange` → returning to the tab triggers
//      another update probe. Catches devices that left the app open
//      overnight.
//   4. Re-check every 15 minutes while the tab is foregrounded so a
//      long-running session catches deploys mid-browse.
//   5. Listen for SW_UPDATED message (legacy path) and `controllerchange`
//      event (the canonical browser signal). Reload once on either.
// ---------------------------------------------------------------------------

if ("serviceWorker" in navigator) {
  let _reloadedOnce = false;
  const reloadOnce = () => {
    if (_reloadedOnce) return;
    _reloadedOnce = true;
    window.location.reload();
  };

  window.addEventListener("load", async () => {
    let registration;
    try {
      registration = await navigator.serviceWorker.register("/sw.js");
    } catch {
      return;
    }
    // iter214 — some browsers (private mode, older Samsung Internet) can
    // resolve `.register()` with `undefined` instead of throwing. Guard
    // every downstream `.addEventListener` — this was the "Cannot read
    // properties of undefined (reading 'addEve…')" Sentry TypeError.
    if (!registration || typeof registration.addEventListener !== "function") {
      return;
    }

    // 1. Kick a fresh update check right after registration so a tab that
    //    was already open at deploy-time picks up the new shell instantly.
    try { await registration.update(); } catch {}

    // 2. When the browser discovers a new SW being installed, watch it
    //    transition to `installed` while we already have a controller.
    //    That's the "fresh version downloaded — ready to swap" moment.
    //    Visible tab → defer activation to a user-tap on FreshContentChip
    //    so we never reload mid-action. Hidden tab → silent skipWaiting
    //    (background tabs don't care about being reloaded).
    registration.addEventListener("updatefound", () => {
      const newSW = registration.installing;
      if (!newSW || typeof newSW.addEventListener !== "function") return;
      newSW.addEventListener("statechange", () => {
        if (newSW.state === "installed" && navigator.serviceWorker.controller) {
          if (document.visibilityState === "hidden") {
            // Background tab — activate silently. controllerchange → reload.
            try { newSW.postMessage({ type: "SKIP_WAITING" }); } catch {}
          } else {
            // Foreground tab — surface the chip, let the user choose.
            try { window._pendingNewSW = newSW; } catch {}
            try { window.dispatchEvent(new CustomEvent("sw:update-available")); } catch {}
          }
        }
      });
    });

    // 3. Re-probe whenever the tab becomes visible (returning user).
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        registration.update().catch(() => {});
      }
    });

    // 4. Poll every 15 minutes while the tab is foregrounded.
    setInterval(() => {
      if (document.visibilityState === "visible") {
        registration.update().catch(() => {});
      }
    }, 15 * 60 * 1000);
  });

  // 5a. SW_UPDATED message from the activate handler (legacy, kept for
  //     Samsung Internet / iOS Safari paths that don't fire controllerchange).
  // iter214 — guard the whole navigator.serviceWorker.addEventListener
  // block: some browsers expose `navigator.serviceWorker` but return a
  // stub without addEventListener (e.g. embedded WebViews with SW
  // partially disabled by the host app).
  if (typeof navigator.serviceWorker.addEventListener === "function") {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "SW_UPDATED") reloadOnce();
    });

    // 5b. The canonical signal: a new SW just took control of this page.
    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce);
  }
}
